import { tt } from '@agos/shared'

/**
 * Which ways the franchise will take money, and for which kind of order.
 *
 * **Measured: `{"isActive": true, "defaultOnline": false, "allowOnlinePickup": true}`.** So all three
 * methods are on offer today — which is exactly why this being absent went unnoticed: the port offered
 * all three unconditionally and happened to be right.
 *
 * Two settings can take one away, and both are a franchise's own switch:
 *
 * - **`isActive`** turns card-online off entirely. Offering it anyway sends the customer to a payment
 *   gateway the restaurant has not configured, and the order is placed and unpaid at the far end.
 * - **`allowOnlinePickup`** turns it off for **pickup** orders only. A restaurant may be happy to take
 *   a card for a delivery it has to send a driver on and not for a bag somebody collects.
 *
 * `defaultOnline` picks which method starts selected. It is not a restriction and does not belong with
 * the other two, but legacy reads all three from one payload and so does this.
 */

export type PaymentMethod = 'cash' | 'card_terminal' | 'card_online'
/**
 * Whether the order goes to an address — §9.50.
 *
 * This was `type: 'delivery' | 'bar'`, a slug, and card-online was offered when it read `'delivery'`.
 * With the channels coming from the franchise that is the wrong question: a *Terasa* would have been
 * treated as neither. What the rule always meant is *is this delivered*, which is the channel's own
 * `requiresDeliveryAddress`, so the capability is passed instead of the name.
 */
export type OrderType = 'delivery' | 'bar'

export interface PaymentSettings {
  /** Whether card-online is offered at all. */
  onlineActive: boolean
  /** Whether card-online is offered on a pickup order. */
  onlineOnPickup: boolean
  /** Whether card-online is the method that starts selected. */
  defaultOnline: boolean
}

export const PAYMENT_FALLBACK: PaymentSettings = {
  // Fails **closed** for the one method that can strand an order: a settings call that did not answer
  // is not a reason to send somebody to a gateway that may not exist. Cash and the terminal are always
  // available — they need nothing configured.
  onlineActive: false,
  onlineOnPickup: true,
  defaultOnline: false,
}

export function paymentSettingsOf(payload: unknown): PaymentSettings {
  if (typeof payload !== 'object' || payload === null) return PAYMENT_FALLBACK
  const row = payload as Record<string, unknown>
  return {
    onlineActive: row.isActive === true,
    // Legacy's `?? true`: the field was added after the feature, so absent means *before anybody
    // chose*, not *off*. It only matters when `isActive` is true anyway.
    onlineOnPickup: row.allowOnlinePickup !== false,
    defaultOnline: row.defaultOnline === true,
  }
}

/** The methods to offer, in the order they are shown. */
export function paymentMethodsFor(settings: PaymentSettings, delivered: boolean): PaymentMethod[] {
  const methods: PaymentMethod[] = ['cash', 'card_terminal']
  if (settings.onlineActive && (delivered || settings.onlineOnPickup)) {
    methods.push('card_online')
  }
  return methods
}

/**
 * Which method should be selected, given what is on offer and what was selected before.
 *
 * The reason this is a function rather than an initial value: **the order type can change after the
 * method was picked.** A customer choosing card-online for a delivery and then switching to pickup, at
 * a restaurant that does not take cards on pickup, would otherwise be left holding a method that is no
 * longer offered — and legacy has an effect that resets it for exactly this case.
 */
export function selectedPaymentMethod(
  settings: PaymentSettings,
  delivered: boolean,
  current: PaymentMethod | null,
): PaymentMethod {
  const offered = paymentMethodsFor(settings, delivered)
  if (current !== null && offered.includes(current)) return current
  if (current === null && settings.defaultOnline && offered.includes('card_online')) return 'card_online'
  return offered[0] ?? 'cash'
}

export function paymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case 'cash':
      return tt('Hotovosť')
    case 'card_terminal':
      // Legacy's own name for it. This port called it *Kartou pri prevzatí*, which is legacy's
      // *description* of the same method — so the label and the line under it were the same sentence
      // twice, and neither matched what legacy calls the thing (FB-C53).
      return tt('Karta – terminál')
    default:
      return tt('Online platba kartou')
  }
}

/** The line under the name. Legacy has one for each, and they are not decoration: *na bare* is where. */
export function paymentMethodNote(method: PaymentMethod): string {
  switch (method) {
    case 'cash':
      return tt('Platba kuriérovi / na bare')
    case 'card_terminal':
      return tt('Platba kartou pri prevzatí')
    default:
      return tt('Zabezpečená online platobná brána')
  }
}
